import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Button, Card, CardContent, Typography, Grid, Chip, Box,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import '../../../../assets/styles/Admin/AdminDashboardOverview.css';
import { BACKEND_DOMAIN } from '../../../../common/env';

const AdminDashboardOverview = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [userHasPaid, setUserHasPaid] = useState(false);
  
  const [newAuction, setNewAuction] = useState({
    title: '',
    sport: 'cricket',
    organizer: '',
    date: '',
    location: '',
    defaultTeamBudgets: '',
    playersPerTeam: '',
    baseValue: '',
    auctionStatus: 'Upcoming'
  });
  
  // Add state for auction logo file
  const [auctionLogoFile, setAuctionLogoFile] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchAuctions();
    fetchUserInfo();
  }, [token]);

  const fetchUserInfo = async () => {
    try {
      if (!token) return;
      
      const response = await axios.get(`${BACKEND_DOMAIN}/my-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setUserHasPaid(response.data.hasPaid);
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      if (!token) {
        setAuctions([]);
        return;
      }
      
      const response = await axios.get(`${BACKEND_DOMAIN}/myAuctions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAuctions(response.data);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAuction = async () => {
    try {
      const auctionData = {
        ...newAuction,
        dateTime: newAuction.date,
        defaultTeamBudgets: Number(newAuction.defaultTeamBudgets),
        playersPerTeam: Number(newAuction.playersPerTeam),
        baseValue: Number(newAuction.baseValue)
      };

      // Create FormData object to send data as multipart/form-data
      const formData = new FormData();
      Object.keys(auctionData).forEach(key => {
        formData.append(key, auctionData[key]);
      });
      
      // Append auction logo file if selected
      if (auctionLogoFile) {
        formData.append('auctionLogo', auctionLogoFile);
      }

      await axios.post(`${BACKEND_DOMAIN}/auctions`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });

      alert('Auction created successfully!');
      setShowCreateModal(false);
      setNewAuction({
        title: '',
        sport: 'cricket',
        organizer: '',
        date: '',
        location: '',
        defaultTeamBudgets: '',
        playersPerTeam: '',
        baseValue: '',
        auctionStatus: 'Upcoming'
      });
      // Reset auction logo file state
      setAuctionLogoFile(null);
      fetchAuctions();
    } catch (error) {
      console.error('Error creating auction:', error);
      alert('Failed to create auction. Please try again.');
    }
  };

  const handleUpdateAuction = async () => {
    try {
      const auctionData = {
        ...selectedAuction,
        defaultTeamBudgets: Number(selectedAuction.defaultTeamBudgets),
        playersPerTeam: Number(selectedAuction.playersPerTeam),
        baseValue: Number(selectedAuction.baseValue)
      };

      await axios.put(`${BACKEND_DOMAIN}/auctions/${selectedAuction._id}`, auctionData, {
        headers: { 
          Authorization: `Bearer ${token}`,
        },
      });

      alert('Auction updated successfully!');
      setShowUpdateModal(false);
      setSelectedAuction(null);
      fetchAuctions();
    } catch (error) {
      console.error('Error updating auction:', error);
      alert('Failed to update auction. Please try again.');
    }
  };

  const handleDeleteAuction = async (auctionId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this auction?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${BACKEND_DOMAIN}/auctions/${auctionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Auction deleted successfully!');
      fetchAuctions();
    } catch (error) {
      console.error('Error deleting auction:', error);
      alert('Failed to delete auction. Please try again.');
    }
  };

  const openUpdateModal = (auction) => {
    setSelectedAuction({
      ...auction,
      date: auction.date ? new Date(auction.date).toISOString().slice(0, 16) : ''
    });
    setShowUpdateModal(true);
  };

  const handleInputChange = (e, isUpdate = false) => {
    const { name, value } = e.target;
    if (isUpdate) {
      setSelectedAuction(prev => ({ ...prev, [name]: value }));
    } else {
      setNewAuction(prev => ({ ...prev, [name]: value }));
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'upcoming': return '#059669';
      case 'ongoing': return '#d97706';
      case 'past': return '#7c3aed';
      default: return '#64748b';
    }
  };

  const getAuctionStatus = (auction) => {
    const currentDate = new Date();
    const auctionDate = new Date(auction.date);
    
    if (auctionDate > currentDate) return 'Upcoming';
    if (auctionDate.toDateString() === currentDate.toDateString()) return 'Ongoing';
    return 'Past';
  };

  // Handle auction selection - navigate to the separate auction details page
  const handleAuctionSelect = (auctionId) => {
    navigate(`/admin-dashboard/auction/${auctionId}`);
  };

  // Filter auctions based on search term
  const filteredAuctions = auctions.filter(auction =>
    auction.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    auction.organizer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    auction.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="admin-dashboard-overview">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  // Handle auction logo file change
  const handleAuctionLogoChange = (e) => {
    setAuctionLogoFile(e.target.files[0]);
  };

  return (
    <div className="admin-dashboard-overview">
      {/* Auction List Header */}
      <Card className="auction-details-card" sx={{ mt: 1 }}>
        <CardContent>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
            <Box></Box>
            <Typography variant="h4" sx={{ 
              flex: 1, 
              order: { xs: 1, sm: 2 }, 
              fontSize: { xs: '1.5rem', sm: '2rem' },
              textAlign: { xs: 'left', sm: 'center' }
            }}>
              My Auctions
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
              disabled={!userHasPaid}
              sx={{ minWidth: { xs: '100%', sm: 'auto' }, order: 3 }}
            >
              Create Auction
            </Button>
          </Box>

          {/* Payment status message */}
          {!userHasPaid && (
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              bgcolor: 'info.light', 
              borderRadius: 1, 
              borderLeft: '4px solid',
              borderColor: 'info.main',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'info.dark' }}>
                🔒 Unlock Full Access
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Please contact our support team to enable auction creation
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                📧 Email: <a href="mailto:contact.myliveauction@gmail.com" style={{ color: '#1976d2', textDecoration: 'none' }}>contact.myliveauction@gmail.com</a>
              </Typography>
            </Box>
          )}

          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search auctions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Auction List - Displaying all auctions at once */}
          <Box sx={{ mt: 2, mb: 3 }}>
            {filteredAuctions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography variant="h6">
                  {searchTerm ? 'No auctions found matching your search' : 'No auctions found'}
                </Typography>
                <Typography variant="body2">
                  {searchTerm ? 'Try adjusting your search terms' : 'Create your first auction to get started'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {filteredAuctions.map((auction) => (
                  <Grid item xs={12} key={auction._id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                      onClick={() => handleAuctionSelect(auction._id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h6" component="h3" sx={{ mr: 2 }}>
                            {auction.title}
                          </Typography>
                          <Chip
                            label={getAuctionStatus(auction)}
                            style={{
                              backgroundColor: getStatusColor(getAuctionStatus(auction)),
                              color: 'white',
                              fontWeight: 600
                            }}
                            size="small"
                          />
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="textSecondary">Organizer</Typography>
                            <Typography variant="body1">{auction.organizer}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="textSecondary">Sport</Typography>
                            <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                              {auction.sport}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="textSecondary">Date</Typography>
                            <Typography variant="body1">
                              {new Date(auction.date).toLocaleDateString()}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="textSecondary">Location</Typography>
                            <Typography variant="body1">{auction.location}</Typography>
                          </Grid>
                        </Grid>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1,
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          mt: 2
                        }}>
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openUpdateModal(auction);
                            }}
                            variant="outlined"
                            className="action-btn edit-btn"
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            startIcon={<PlayArrowIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/live-auction/${auction._id}/admin-live-bidding`);
                            }}
                            variant="contained"
                            className="action-btn live-btn"
                          >
                            Go Live
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAuction(auction._id);
                            }}
                            variant="outlined"
                            color="error"
                            className="action-btn delete-btn"
                          >
                            Delete
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Create Auction Modal */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Auction</DialogTitle>
        <DialogContent>
          <TextField
            label="Auction Title"
            name="title"
            value={newAuction.title}
            onChange={(e) => handleInputChange(e)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Sport"
            name="sport"
            select
            value={newAuction.sport}
            onChange={(e) => handleInputChange(e)}
            fullWidth
            margin="normal"
          >
            <option value="cricket">Cricket</option>
            <option value="football">Football</option>
            <option value="basketball">Basketball</option>
          </TextField>
          <TextField
            label="Organizer"
            name="organizer"
            value={newAuction.organizer}
            onChange={(e) => handleInputChange(e)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Date & Time"
            name="date"
            type="datetime-local"
            value={newAuction.date}
            onChange={(e) => handleInputChange(e)}
            fullWidth
            margin="normal"
            required
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Location"
            name="location"
            value={newAuction.location}
            onChange={(e) => handleInputChange(e)}
            fullWidth
            margin="normal"
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Default Team Budgets ($)"
                name="defaultTeamBudgets"
                type="number"
                value={newAuction.defaultTeamBudgets}
                onChange={(e) => handleInputChange(e)}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Players per Team"
                name="playersPerTeam"
                type="number"
                value={newAuction.playersPerTeam}
                onChange={(e) => handleInputChange(e)}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
          </Grid>
          <TextField
            label="Base Value ($)"
            name="baseValue"
            type="number"
            value={newAuction.baseValue}
            onChange={(e) => handleInputChange(e)}
            fullWidth
            margin="normal"
            required
          />
          <input
            type="file"
            name="auctionLogo"
            onChange={handleAuctionLogoChange}
            accept="image/*"
            style={{ marginTop: '16px', marginBottom: '8px' }}
          />
          <Typography variant="caption" display="block" gutterBottom>
            Auction Logo
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateModal(false)}>Cancel</Button>
          <Button onClick={handleCreateAuction} variant="contained" color="primary">Create Auction</Button>
        </DialogActions>
      </Dialog>

      {/* Update Auction Modal */}
      <Dialog open={showUpdateModal} onClose={() => setShowUpdateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Auction</DialogTitle>
        <DialogContent>
          <TextField
            label="Auction Title"
            name="title"
            value={selectedAuction?.title || ''}
            onChange={(e) => handleInputChange(e, true)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Sport"
            name="sport"
            select
            value={selectedAuction?.sport || 'cricket'}
            onChange={(e) => handleInputChange(e, true)}
            fullWidth
            margin="normal"
          >
            <option value="cricket">Cricket</option>
            <option value="football">Football</option>
            <option value="basketball">Basketball</option>
          </TextField>
          <TextField
            label="Organizer"
            name="organizer"
            value={selectedAuction?.organizer || ''}
            onChange={(e) => handleInputChange(e, true)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Date & Time"
            name="date"
            type="datetime-local"
            value={selectedAuction?.date || ''}
            onChange={(e) => handleInputChange(e, true)}
            fullWidth
            margin="normal"
            required
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            label="Location"
            name="location"
            value={selectedAuction?.location || ''}
            onChange={(e) => handleInputChange(e, true)}
            fullWidth
            margin="normal"
            required
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Default Team Budgets ($)"
                name="defaultTeamBudgets"
                type="number"
                value={selectedAuction?.defaultTeamBudgets || ''}
                onChange={(e) => handleInputChange(e, true)}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Players per Team"
                name="playersPerTeam"
                type="number"
                value={selectedAuction?.playersPerTeam || ''}
                onChange={(e) => handleInputChange(e, true)}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
          </Grid>
          <TextField
            label="Base Value ($)"
            name="baseValue"
            type="number"
            value={selectedAuction?.baseValue || ''}
            onChange={(e) => handleInputChange(e, true)}
            fullWidth
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpdateModal(false)}>Cancel</Button>
          <Button onClick={handleUpdateAuction} variant="contained" color="primary">Update Auction</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminDashboardOverview;